/**
 * AgentToolPicker — chip multi-select for ``agent_config.tools`` powered by
 * ``GET /tools``. Replaces the legacy free-form ``TagInput`` so a Creator
 * can:
 *
 *   - see only the MCP tools registered for this workspace,
 *   - select one or many in the same field (multi-tag chips),
 *   - get a config/auth dialog **only** when the tool needs stored credentials or
 *     the structured generic-REST wizard (secrets / ``x-secret`` /
 *     ``generic-rest-api``) — invocation-only JSON Schema ``required``
 *     fields stay with the runtime LLM, not the picker,
 *   - ``code-connector`` always opens the registry attach dialog (pick a
 *     TESTED_OK Connection — never a bare tool id),
 *   - re-open the editor from an **Edit** control on typed chips or pick the
 *     tool twice with distinct ``instanceLabel`` values.
 *
 * The accepted output shape on ``onChange`` is the array the agent PUT
 * validator (``api.rest.tools.agent_tools_validator``) understands —
 * either a bare string for tools with no persisted auth/instance config,
 * or ``{ name, instanceId, instanceLabel, config, secretRefs, secretId?, secretName? }`` 
 * when using the generic-REST wizard / registry attach (``secretId`` ties a row in
 * ``GET /tools/secrets-registry``; the resolver hydrates ``config`` / ``secretRefs`` server-side).
 *
 * Suggestions are rendered through ``PortalMenu`` so the popover is not
 * clipped by the parent scrollable panel (workbench rules:
 * ``.cursor/rules/frontend-dropdown-portal.mdc``).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ToolSelectDialog from './ToolSelectDialog.jsx';
import ToolConfigDialog from './ToolConfigDialog.jsx';
import McpCredentialWizardDialog from '../mcp/McpCredentialWizardDialog.jsx';
import {
  newToolInstanceId,
  createMcpCredential,
  testMcpCredentialScoped,
  revealMcpCredentialScoped,
} from '../../services/toolsApi';
import { scopeBadgeLabel, scopeBadgeClass, scopeBadgeTitle } from '../../utils/scopeBadge';

function entryName(entry) {
  if (typeof entry === 'string') return entry;
  return entry?.name || '';
}

/** Normalize an enabledMcpIds entry (string | object) → {id, instanceId, label}. */
function _normMcp(x) {
  if (typeof x === 'string') {
    const id = x.trim();
    return id ? { id, instanceId: 'default', label: id } : null;
  }
  if (x && typeof x === 'object') {
    const id = String(x.id || x.mcpId || '').trim();
    if (!id) return null;
    return {
      id,
      instanceId: String(x.instanceId || 'default').trim() || 'default',
      label: String(x.label || id).trim(),
    };
  }
  return null;
}

function entryInstanceId(entry) {
  if (typeof entry === 'string') return null;
  return entry?.instanceId || null;
}

function chipKey(entry, idx) {
  const id = entryInstanceId(entry);
  if (id) return `id:${id}`;
  return `name:${entryName(entry)}:${idx}`;
}

function chipRowTestId(entry, idx) {
  const id = entryInstanceId(entry);
  if (id) return String(id).replace(/[^a-zA-Z0-9_-]/g, '_');
  if (typeof entry === 'string') {
    return `str_${String(entry).replace(/[^a-zA-Z0-9_-]/g, '_')}_${idx}`;
  }
  return `idx_${idx}`;
}

function entryHasSecretRefs(entry) {
  if (typeof entry !== 'object' || !entry?.secretRefs || typeof entry.secretRefs !== 'object') {
    return false;
  }
  return Object.keys(entry.secretRefs).length > 0;
}

function entryHasSensitiveAttachment(entry) {
  if (typeof entry !== 'object' || !entry) return false;
  if (String(entry.secretId || '').trim()) return true;
  return entryHasSecretRefs(entry);
}

/** Chip headline — ``instanceLabel`` when set; otherwise MCP tool ``name``. */
function chipPrimaryLabel(entry) {
  if (typeof entry === 'string') return entry;
  const lab = typeof entry.instanceLabel === 'string' ? entry.instanceLabel.trim() : '';
  const n = entry?.name || '';
  return lab || n;
}

/** Shown after an interpunct when the headline is the custom label. */
function chipToolNameSuffix(entry) {
  if (typeof entry !== 'object' || !entry) return null;
  const lab = typeof entry.instanceLabel === 'string' ? entry.instanceLabel.trim() : '';
  if (!lab || !entry.name) return null;
  return entry.name;
}

function schemaHasStoredSecretHints(schema) {
  if (!schema || typeof schema !== 'object') return false;
  const props = schema.properties;
  if (!props || typeof props !== 'object') return false;
  return Object.values(props).some((s) => Boolean(s && typeof s === 'object' && s['x-secret'] === true));
}

/**
 * InstanceListSubview — the ONE "pick a named connection" sub-view shared by
 * MCP, connector, and workspace selection. Config complexity is normalized away
 * at each wizard; assignment is uniformly "choose a named instance", so all
 * three reduce to this list over ``{ key, label, tag, disabled, disabledReason }``.
 *
 *   onBack        : () => void        — "← Back to all tools"
 *   items         : [{ key, label, tag?, disabled?, disabledReason? }]
 *   onPick        : (key) => void
 *   emptyHint     : node              — shown when items is empty
 *   testidPrefix  : e.g. "agent-mcp-instance-<scope>"
 *   onCreate?     : () => void        — optional "+ Create new connection"
 *   hoverClass?   : tailwind hover ring color (per type)
 */
export function InstanceListSubview({ onBack, items = [], onPick, emptyHint, testidPrefix, onCreate = null, createLabel = '+ Create new connection', hoverClass = 'hover:border-blue-400 hover:bg-blue-50/40', banner = null, showBack = true }) {
  return (
    <>
      {showBack && onBack ? (
        <button type="button" onClick={onBack} className="mb-3 text-[11px] text-blue-600 hover:underline">
          ← Back to all tools
        </button>
      ) : null}
      {banner ? (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">{banner}</div>
      ) : null}
      {items.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-800">{emptyHint}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              disabled={Boolean(it.disabled)}
              onClick={() => onPick(it.key)}
              data-testid={it.testid || `${testidPrefix}-${it.key}`}
              title={it.disabled ? (it.disabledReason || '') : ''}
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-xs ${
                it.disabled ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : `border-gray-200 ${hoverClass}`
              }`}
            >
              <span className="font-medium text-gray-800">{it.label}</span>
              <span className="flex items-center gap-1.5">
                {it.scope ? (
                  <span
                    className={`rounded px-1.5 py-px text-[9px] font-medium uppercase tracking-wide ring-1 ${scopeBadgeClass(it.scope)}`}
                    title={scopeBadgeTitle(it.scope)}
                  >
                    {scopeBadgeLabel(it.scope)}
                  </span>
                ) : null}
                {it.tag ? <span className="text-[10px] text-gray-500">{it.tag}</span> : null}
              </span>
            </button>
          ))}
        </div>
      )}
      {onCreate ? (
        <button type="button" onClick={onCreate} data-testid={`${testidPrefix}-create`}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-700">
          {createLabel}
        </button>
      ) : null}
    </>
  );
}

/** True only when opening ``ToolConfigDialog`` / wizard is warranted (persisted secrets or generic-REST wizard). */
function needsToolConfiguratorDialog(tool) {
  if (!tool) return false;
  if (tool.name === 'generic-rest-api') return true;
  /** Registry-backed git connector — never a bare string; must pick a TESTED_OK row like generic-rest attach. */
  if (tool.name === 'code-connector') return true;
  if (Array.isArray(tool.secretFields) && tool.secretFields.length > 0) return true;
  return schemaHasStoredSecretHints(tool.inputSchema);
}

/**
 * @param {{
 *   value: Array<string | object>,
 *   onChange: (next: Array<string | object>) => void,
 *   tools: Array<{ name: string, description?: string, inputSchema?: object, secretFields?: string[], enabled?: boolean }>,
 *   loading?: boolean,
 *   error?: string,
 *   networkId: string,
 *   poolId?: string,   // room id — lets Edit prefer the room's own POOL-scoped
 *                      // MCP instance over the network's when both share
 *                      // instanceId 'default' (avoids revealing network creds).
 *   agentId: string,
 *   env: string,
 *   tenantId: string,
 *   placeholder?: string,
 * }} props
 */
export default function AgentToolPicker({
  value,
  onChange,
  tools = [],
  loading = false,
  error = '',
  networkId,
  poolId,
  agentId,
  env,
  tenantId,
  placeholder = 'Add a tool…',
  // ── Optional MCP support (off by default) ───────────────────────────────
  // When ``mcpCatalog`` + ``onMcpChange`` are provided, authenticated/
  // unauthenticated MCPs appear in the SAME typeahead + chip strip as
  // connector tools. MCP entries live in a SEPARATE field (``mcpValue`` →
  // ``onMcpChange``, the agent's ``enabledMcpIds``), shaped
  // ``{id, instanceId, label}``. Picking an authenticated MCP opens an instance
  // dropdown of configured Connections; an unauthenticated one attaches直接.
  // Callers that omit these props (e.g. the connector-override matrix) are
  // completely unaffected.
  mcpCatalog = null,
  mcpValue = [],
  onMcpChange = null,
  mcpInstancesLoader = null, // async () => { [mcpId]: [{instanceId,label,scope}] }
  // ── Optional WORKSPACE support (off by default) ─────────────────────────
  // Like the MCP trio, but SINGLE-slot (one shared workspace per agent — it's
  // the agent's on-disk working tree, not a multi-pick tool). When
  // ``workspaceCatalog`` + ``onWorkspaceChange`` are provided, the curated
  // workspaces appear in the SAME picker; ``workspaceValue`` is the current
  // single id ('' = none). Picking REPLACES it; at most one workspace chip.
  // Callers that omit these are unaffected. See doc/arch/TEMPLATES.md.
  workspaceCatalog = null,   // [{ id, label, description }]
  workspaceValue = '',       // single workspace id, or ''
  onWorkspaceChange = null,  // (idOrEmpty) => void
  // ── Optional CONNECTOR instance picker (off by default) ─────────────────
  // When provided, a credentialed connector tile (code-connector / generic-
  // rest-api) opens an in-modal LIST of the user's SAVED connections (registry
  // secrets) — the same type-tile→instance-list flow as MCP/workspace — instead
  // of jumping straight to the create wizard. Loader returns
  // ``{ [toolName]: [{secretId, secretName, testStatus, scope}] }``. A "+ Create
  // new connection" affordance still opens the wizard. Callers that omit this
  // keep the old wizard-on-click behavior (unaffected).
  connectorInstancesLoader = null,
  // ── Optional "Create in Connections" navigation (off by default) ─────────
  // When provided, an empty instance list (Shared Workspace / MCP) shows a
  // "+ Create in Connections" button that calls this with a hint so the host
  // can switch to the Connections tab and auto-open the right wizard. Shape:
  // ``({ tool }) => void`` where ``tool`` is 'workspace' | the MCP id. Omitting
  // it falls back to the plain text empty-hint (unaffected). The connector tile
  // already has an in-modal create, so it doesn't use this.
  onNavigateToConnections = null,
  // Round-trip return: a connection just created in Connections, to auto-assign
  // here. ``{ tool, secretId, secretName }`` (tool = 'workspace' | MCP id |
  // connector toolName). Consumed once → ``onPendingToolAssignConsumed``.
  pendingToolAssign = null,
  onPendingToolAssignConsumed = null,
}) {
  const entries = Array.isArray(value) ? value : [];
  const mcpEnabled = Array.isArray(mcpCatalog) && typeof onMcpChange === 'function';
  const mcpEntries = mcpEnabled && Array.isArray(mcpValue)
    ? mcpValue.map((v) => _normMcp(v)).filter(Boolean)
    : [];
  const wsEnabled = Array.isArray(workspaceCatalog) && typeof onWorkspaceChange === 'function';
  const wsId = wsEnabled ? String(workspaceValue || '').trim() : '';
  const wsCurrent = wsId ? (workspaceCatalog.find((w) => String(w.id) === wsId) || { id: wsId, label: wsId }) : null;
  // OVERLAP: a code-connector and a workspace both claim the agent's on-disk
  // /workspace/repo. The builder resolves connector > workspace, so when BOTH
  // are set the workspace is silently IGNORED at run time. Detect it so we can
  // warn (banner), gate (confirm), and tag the overridden chip — never silent.
  // See doc/arch/TEMPLATES.md §"Tool overlap".
  const hasConnector = entries.some((e) => entryName(e) === 'code-connector');
  const workspaceOverridden = Boolean(wsCurrent) && hasConnector;
  const [mcpChoose, setMcpChoose] = useState(null); // registry row whose instances we're picking
  const [wsChoose, setWsChoose] = useState(false);  // showing the created-workspace list sub-view
  const [connChoose, setConnChoose] = useState(null); // connector tool whose saved connections we're picking
  const [connInstances, setConnInstances] = useState({}); // { [toolName]: [{secretId,secretName,testStatus,scope}] }
  const [mcpInstances, setMcpInstances] = useState({});
  const connEnabled = typeof connectorInstancesLoader === 'function';
  const [pickerModal, setPickerModal] = useState(false); // add-tool modal open?
  // Inline MCP credential EDIT (Option A — full parity with Connections "Edit").
  // { mcp, scope, instanceId, label, initialValues, initialSecretsSet } — when
  // set, the shared McpCredentialWizardDialog opens pre-filled on THIS instance.
  const [mcpEditWizard, setMcpEditWizard] = useState(null);
  const [mcpEditBusy, setMcpEditBusy] = useState(false); // reveal in flight (chip disabled)
  const [dialog, setDialog] = useState(null); // { tool, isNewEntry, entryIndex, initialEntry }
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState(null);
  // Confirm dialog for an overlap-creating pick: { kind:'workspace', id } when
  // picking a workspace while a connector exists. (connector-while-workspace is
  // handled symmetrically below.)
  const [conflictPrompt, setConflictPrompt] = useState(null);

  const enabledTools = useMemo(
    () => (Array.isArray(tools) ? tools.filter((t) => t.enabled !== false) : []),
    [tools],
  );

  const selectedNames = useMemo(() => new Set(entries.map(entryName)), [entries]);

  const findTool = useCallback(
    (name) => enabledTools.find((t) => t.name === name) || null,
    [enabledTools],
  );

  const addEntry = (tool) => {
    if (needsToolConfiguratorDialog(tool)) {
      // Opens the config wizard; modal stays mounted under it, then closes.
      setPickerModal(false);
      setDialog({ tool, isNewEntry: true, entryIndex: -1, initialEntry: undefined });
      return;
    }
    if (selectedNames.has(tool.name)) return;
    onChange([...entries, tool.name]);
    setPickerModal(false);
  };

  // Connector type-tile → in-modal list of the user's saved connections for that
  // connector (mirrors pickMcp). Lazy-load once per open.
  const pickConnector = async (tool) => {
    setConnChoose(tool);
    if (connectorInstancesLoader && connInstances[tool.name] === undefined) {
      try {
        const all = await connectorInstancesLoader();
        setConnInstances(all || {});
      } catch {
        setConnInstances((p) => ({ ...p, [tool.name]: [] }));
      }
    }
  };

  // Attach a SAVED connector connection (registry secret) — emits the entry with
  // ``secretId`` directly, bypassing the create wizard (the common case).
  const attachConnector = (tool, inst, { force = false } = {}) => {
    // A code-connector takes the on-disk tree — if a workspace is pinned it'll
    // be silently ignored at run time. Confirm before creating that overlap.
    // ``force`` skips the guard when invoked from the confirm dialog itself.
    if (!force && tool.name === 'code-connector' && wsCurrent) {
      setConflictPrompt({ kind: 'connector', tool, inst });
      return;
    }
    const entry = {
      name: tool.name,
      instanceId: newToolInstanceId(),
      instanceLabel: inst.secretName || String(inst.secretId || '').slice(0, 8),
      secretId: inst.secretId,
      secretName: inst.secretName,
    };
    onChange([...entries, entry]);
    setPickerModal(false);
    setConnChoose(null);
  };

  // "+ Create new connection" from the list → the existing create wizard.
  const openConnectorCreate = (tool) => {
    setConnChoose(null);
    setPickerModal(false);
    setDialog({ tool, isNewEntry: true, entryIndex: -1, initialEntry: undefined });
  };

  const mcpAttached = (id, instanceId) =>
    mcpEntries.some((e) => e.id === id && e.instanceId === instanceId);

  const attachMcp = (mcp, instanceId, label) => {
    if (!onMcpChange || mcpAttached(mcp.id, instanceId)) return;
    onMcpChange([...mcpEntries, { id: mcp.id, instanceId, label: label || mcp.displayName || mcp.id }]);
  };

  const detachMcpAt = (idx) => {
    if (!onMcpChange) return;
    const next = mcpEntries.slice();
    next.splice(idx, 1);
    onMcpChange(next);
  };

  // Edit a chosen MCP instance → re-open the modal straight to that MCP's
  // configured-instance list so the user can swap to a different connection
  // (mirrors the connector chip "Edit" re-opening its config). Removing the
  // current entry first lets them re-pick (including the same one).
  // Chip "Edit" = the MCP + instance are ALREADY known, so go STRAIGHT to editing
  // THIS instance's credential config (full parity with Connections "Edit"):
  // resolve the instance's scope, ``reveal`` its saved non-secret values, and open
  // the shared credential wizard pre-filled. No detach, no tool re-pick. Save
  // reuses the same ``instanceId`` (edit-in-place). Unauth MCPs have no credential
  // to edit, so their chip never renders an Edit button (guarded on ``authRequired``).
  const editMcpAt = useCallback(async (idx) => {
    const e = mcpEntries[idx];
    if (!e) return;
    const reg = (mcpCatalog || []).find((m) => m.id === e.id);
    if (!reg || !reg.authRequired) return;
    setMcpEditBusy(true);
    // Resolve the instance's SCOPE from the loaded instance list (same source the
    // instance sub-view uses). Fall back to a NETWORK scope keyed to this network
    // when the list hasn't loaded / the row is absent, so Edit still works.
    //
    // instanceId 'default' COLLIDES across scopes: a room can have BOTH the
    // network's github (scope NETWORK, e.g. "ursa GitHub", credentialed) AND its
    // own room-wide github (scope POOL, e.g. "gizmo GitHub", empty) — both at
    // instanceId 'default'. A naive first-match would resolve to the NETWORK row
    // and reveal/overwrite the network's credentials. Rank most-specific first
    // (POOL ▸ NETWORK ▸ TENANT) so editing the room's own shell targets the
    // room's row — mirrors PoolSettingsDrawer.mcpPinLabel, which is why the chip
    // label already reads the room-scoped name.
    let scope = null;
    try {
      const byId = (mcpInstancesLoader ? await mcpInstancesLoader() : mcpInstances) || {};
      const rank = { POOL: poolId ? 3 : 0, NETWORK: 2, TENANT: 1 };
      const matches = (byId[e.id] || []).filter((i) => (i.instanceId || 'default') === (e.instanceId || 'default'));
      let best = null;
      let bestRank = -1;
      for (const i of matches) {
        const rk = rank[String(i.scope?.kind || '').toUpperCase()] || 0;
        if (rk > bestRank) { best = i; bestRank = rk; }
      }
      scope = best?.scope || null;
    } catch { /* fall through to default scope */ }
    if (!scope) scope = { kind: 'NETWORK', id: networkId };
    // Rehydrate saved NON-SECRET values so the wizard opens populated (secrets stay
    // masked). Soft-fail: still open the wizard blank rather than block editing.
    let revealed = null;
    try {
      revealed = await revealMcpCredentialScoped({ mcpId: e.id, scope, instanceId: e.instanceId });
    } catch (err) {
      console.warn('[AgentToolPicker] MCP reveal failed:', err);
    } finally {
      setMcpEditBusy(false);
    }
    setMcpEditWizard({
      mcp: reg,
      scope,
      instanceId: e.instanceId,
      label: revealed?.label || e.label || reg.displayName || e.id,
      initialValues: revealed?.values || null,
      initialSecretsSet: revealed?.secretsSet || null,
    });
  }, [mcpEntries, mcpCatalog, mcpInstancesLoader, mcpInstances, networkId, poolId]);

  // Save/test for the inline edit wizard — routed to the scoped catalog endpoints
  // exactly like Connections. Editing reuses the existing ``instanceId`` (updates
  // THIS instance, never mints a new one).
  const handleMcpEditSave = useCallback(async (mcpId, credentials, opts = {}) => {
    if (!mcpEditWizard) return;
    const label = String(opts.label || '').trim();
    const body = { mcpId, scope: mcpEditWizard.scope, credentials };
    if (label) body.label = label;
    if (mcpEditWizard.instanceId) body.instanceId = mcpEditWizard.instanceId;
    await createMcpCredential(body);
    // Refresh the instance list cache so labels/scopes stay current, then close.
    if (mcpInstancesLoader) {
      try { setMcpInstances((await mcpInstancesLoader()) || {}); } catch { /* best-effort */ }
    }
    // Keep the chip label in sync if the instance was relabelled.
    if (label && onMcpChange) {
      onMcpChange(mcpEntries.map((e) =>
        (e.id === mcpId && e.instanceId === mcpEditWizard.instanceId) ? { ...e, label } : e));
    }
    setMcpEditWizard(null);
  }, [mcpEditWizard, mcpInstancesLoader, mcpEntries, onMcpChange]);

  const handleMcpEditTest = useCallback(
    async (mcpId, credentials) => testMcpCredentialScoped({ mcpId, scope: mcpEditWizard?.scope, credentials }),
    [mcpEditWizard],
  );

  // Round-trip return: a connection was just created in Connections; assign it
  // to THIS agent (the same agent is still selected — panels stay mounted). We
  // have the new id + name on ``pendingToolAssign`` directly, so we don't need
  // it to be in the cached instance lists yet. Fires once, then signals consume.
  useEffect(() => {
    const p = pendingToolAssign;
    if (!p || !p.tool || !p.secretId) return;
    const tool = String(p.tool).trim();
    const secretId = String(p.secretId).trim();
    const secretName = p.secretName || secretId;
    try {
      if (tool === 'workspace') {
        if (wsEnabled && wsId !== secretId) onWorkspaceChange?.(secretId);
      } else if (tool === 'code-connector' || tool === 'generic-rest-api') {
        // Mirror attachConnector's entry shape (skip the workspace-overlap
        // confirm here — creating it implies intent; the chip still flags overlap).
        const exists = entries.some(
          (e) => entryName(e) === tool && (e.secretId === secretId),
        );
        if (!exists) {
          onChange?.([...entries, {
            name: tool,
            instanceId: newToolInstanceId(),
            instanceLabel: secretName,
            secretId,
            secretName,
          }]);
        }
      } else if (mcpEnabled) {
        // MCP id. Attach {id, instanceId, label}; instanceId is the created one.
        const reg = (mcpCatalog || []).find((m) => m.id === tool) || { id: tool, displayName: tool };
        const already = mcpEntries.some((e) => e.id === tool && e.instanceId === secretId);
        if (!already) {
          onMcpChange?.([...mcpEntries, { id: tool, instanceId: secretId, label: secretName || reg.displayName || tool }]);
        }
      }
    } finally {
      // Open the picker so the user sees the freshly-attached chip in context.
      setPickerModal(false);
      onPendingToolAssignConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingToolAssign]);

  const pickMcp = async (mcp) => {
    if (!mcp.authRequired) {
      attachMcp(mcp, 'default', mcp.displayName);
      setPickerModal(false);
      return;
    }
    // Authenticated → load + show its configured instances (in-modal).
    setMcpChoose(mcp);
    if (mcpInstancesLoader && mcpInstances[mcp.id] === undefined) {
      try {
        const all = await mcpInstancesLoader();
        setMcpInstances(all || {});
      } catch {
        setMcpInstances((p) => ({ ...p, [mcp.id]: [] }));
      }
    }
  };

  // Unified selection entries for the SHARED ToolSelectDialog (same shape +
  // same view as Connections "Create New"). MCP entries carry ``__mcp`` so the
  // pick handler routes to the instance sub-view / one-click enable; connector
  // entries carry ``__tool``. The FULL MCP catalog is shown (incl. unauth) so
  // the tool list + counts match every other surface — no authRequired filter.
  const selectEntries = useMemo(() => {
    const out = [];
    if (mcpEnabled) {
      for (const m of (mcpCatalog || [])) {
        if (!m || !m.id) continue;
        out.push({
          category: 'mcp',
          name: `mcp:${m.id}`,
          displayName: m.displayName || m.id,
          description: m.description || '',
          authRequired: Boolean(m.authRequired),
          comingSoon: Boolean(m.comingSoon),
          mostUsed: Boolean(m.mostUsed),
          verified: Boolean(m.verified),
          __mcp: m,
        });
      }
    }
    for (const t of enabledTools) {
      if (!t || !t.name) continue;
      const needsCfg = needsToolConfiguratorDialog(t);
      // Hide a connector already attached (unless it re-opens a config wizard).
      if (selectedNames.has(t.name) && !needsCfg) continue;
      const hasSecretFields = Array.isArray(t.secretFields) && t.secretFields.length > 0;
      let badge = 'Ready';
      if (t.name === 'code-connector') badge = 'Requires saved git connection';
      else if (hasSecretFields) badge = 'Requires secrets';
      else if (needsCfg) badge = 'Configure before use';
      out.push({
        category: 'connector',
        name: t.name,
        displayName: t.name,
        description: t.description || '',
        comingSoon: false,
        badge,
        badgeClass: needsCfg ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700',
        __tool: t,
      });
    }
    // ONE "Shared Workspace" TYPE tile (mirrors an MCP/connector type tile —
    // NOT one tile per created workspace). Picking it opens an in-modal list of
    // the user's created workspaces to choose from (like GitHub → its connections).
    if (wsEnabled) {
      out.push({
        category: 'connector',
        name: 'workspace',
        displayName: 'Shared Workspace',
        description: 'A shared, mutable workspace — edits sync back and replace the prior contents; concurrent runs are serialized. Pick one of your created workspaces.',
        comingSoon: false,
        badge: wsId ? 'Replace current' : 'Shared workspace',
        badgeClass: 'bg-indigo-50 text-indigo-700',
        __workspaceType: true,
      });
    }
    return out;
  }, [mcpEnabled, mcpCatalog, enabledTools, selectedNames, entries, mcpEntries, wsEnabled, wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route a pick from the shared dialog to the right post-selection action.
  const onSelectEntry = (entry) => {
    if (entry?.__mcp) { pickMcp(entry.__mcp); return; }   // → MCP instance sub-view or 1-click enable
    if (entry?.__workspaceType) { setWsChoose(true); return; } // → created-workspace list sub-view
    if (entry?.__tool) {
      // Credentialed connector + a loader → show its saved-connection list
      // (type-tile→instance-list, same as MCP/workspace). Else old wizard path.
      if (connEnabled && needsToolConfiguratorDialog(entry.__tool)) {
        pickConnector(entry.__tool);
        return;
      }
      addEntry(entry.__tool);
      return;
    }
  };

  const removeEntryAt = (idx) => {
    const next = entries.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const promptRemoveEntryAt = (idx) => {
    const entry = entries[idx];
    if (typeof entry !== 'object' || !entryHasSensitiveAttachment(entry)) {
      removeEntryAt(idx);
      return;
    }
    setPendingRemoveIdx(idx);
  };

  const confirmPendingRemove = () => {
    if (pendingRemoveIdx === null) return;
    removeEntryAt(pendingRemoveIdx);
    setPendingRemoveIdx(null);
  };

  const cancelPendingRemove = () => setPendingRemoveIdx(null);

  const editEntryAt = (idx) => {
    const entry = entries[idx];
    const tool = findTool(entryName(entry));
    if (!tool) return;
    if (typeof entry === 'string') {
      setDialog({ tool, isNewEntry: false, entryIndex: idx, initialEntry: undefined });
    } else {
      setDialog({ tool, isNewEntry: false, entryIndex: idx, initialEntry: entry });
    }
  };

  const handleDialogSave = (entry) => {
    if (!dialog) return;
    if (dialog.entryIndex >= 0) {
      const next = entries.slice();
      next[dialog.entryIndex] = entry;
      onChange(next);
    } else {
      onChange([...entries, entry]);
    }
    setDialog(null);
  };

  const handleDialogCancel = () => {
    // Spec: cancelling on a NEW entry must drop the chip — the picker
    // never committed it to ``entries`` so this is just "close + clear".
    setDialog(null);
  };

  return (
    <>
      <div
        className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-md bg-white min-h-[38px]"
        data-testid="agent-tool-picker"
      >
        {entries.map((entry, idx) => {
          const name = entryName(entry);
          const tool = findTool(name);
          const typed = tool && needsToolConfiguratorDialog(tool);
          const tid = chipRowTestId(entry, idx);
          const primary = chipPrimaryLabel(entry);
          const suffix = chipToolNameSuffix(entry);
          const stripTitle = suffix ? `${primary} · ${suffix}` : primary;
          return (
            <span
              key={chipKey(entry, idx)}
              className="inline-flex items-center gap-2 pl-2.5 pr-1 py-0.5 max-w-[min(100%,22rem)] bg-blue-100 text-blue-900 text-xs rounded-full"
              data-testid={`agent-tool-chip-${tid}`}
              title={stripTitle}
            >
              <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                <span className="font-semibold truncate">{primary}</span>
                {suffix ? (
                  <>
                    <span aria-hidden className="shrink-0 text-blue-900/35">
                      ·
                    </span>
                    <span className="shrink-0 truncate font-normal text-[10px] text-blue-900/70">{suffix}</span>
                  </>
                ) : null}
              </span>
              {typed ? (
                <button
                  type="button"
                  onClick={() => editEntryAt(idx)}
                  data-testid={`agent-tool-edit-${tid}`}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800 underline decoration-blue-700/60 underline-offset-2 hover:bg-blue-200/50 focus:outline-none"
                >
                  Edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => promptRemoveEntryAt(idx)}
                aria-label={`Remove ${stripTitle}`}
                className="shrink-0 px-1 text-blue-800 hover:text-blue-950 focus:outline-none"
              >
                ×
              </button>
            </span>
          );
        })}
        {mcpEntries.map((e, idx) => {
          const reg = (mcpCatalog || []).find((m) => m.id === e.id) || {};
          // Headline = the instance label when we have one, else the registry
          // display name. We prefer an explicit ``e.label`` even for the
          // ``default`` instance — a room-scoped shell (e.g. "ursa GitHub") lives
          // at the default instance but carries a room-specific label; the old
          // ``instanceId !== 'default'`` guard wrongly discarded it and showed the
          // generic registry name.
          const headline = (e.label && e.label !== e.id)
            ? e.label
            : (reg.displayName || e.id);
          const typeTag = `mcp:${e.id}`;
          return (
            <span
              key={`mcp:${e.id}:${e.instanceId}`}
              className="inline-flex items-center gap-2 pl-2.5 pr-1 py-0.5 max-w-[min(100%,24rem)] bg-emerald-100 text-emerald-900 text-xs rounded-full"
              data-testid={`agent-mcp-chip-${e.id}_${e.instanceId}`}
              title={`${headline} · ${typeTag}`}
            >
              <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                <span className="font-semibold truncate">{headline}</span>
                <span aria-hidden className="shrink-0 text-emerald-900/35">·</span>
                <span className="shrink-0 truncate font-mono text-[10px] text-emerald-900/70">{typeTag}</span>
              </span>
              {reg.authRequired ? (
                <button
                  type="button"
                  onClick={() => editMcpAt(idx)}
                  disabled={mcpEditBusy}
                  data-testid={`agent-mcp-edit-${e.id}_${e.instanceId}`}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 underline decoration-emerald-700/60 underline-offset-2 hover:bg-emerald-200/50 focus:outline-none disabled:opacity-50"
                  title="Edit this connection's credential"
                >
                  Edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => detachMcpAt(idx)}
                aria-label={`Remove ${reg.displayName || e.id}`}
                data-testid={`agent-mcp-remove-${e.id}_${e.instanceId}`}
                className="shrink-0 px-1 text-emerald-800 hover:text-emerald-950 focus:outline-none"
              >
                ×
              </button>
            </span>
          );
        })}
        {wsCurrent ? (
          <span
            className={`inline-flex items-center gap-2 pl-2.5 pr-1 py-0.5 max-w-[min(100%,26rem)] text-xs rounded-full ${
              workspaceOverridden ? 'bg-gray-100 text-gray-500' : 'bg-indigo-100 text-indigo-900'
            }`}
            data-testid={`agent-workspace-chip-${wsCurrent.id}`}
            title={workspaceOverridden
              ? `${wsCurrent.label || wsCurrent.id} · overridden by the git connector (won't run)`
              : `${wsCurrent.label || wsCurrent.id} · workspace`}
          >
            <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
              <span className={`font-semibold truncate ${workspaceOverridden ? 'line-through' : ''}`}>{wsCurrent.label || wsCurrent.id}</span>
              <span aria-hidden className="shrink-0 text-current/35">·</span>
              <span className="shrink-0 font-mono text-[10px] text-current/70">
                {workspaceOverridden ? 'overridden by connector' : 'workspace'}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onWorkspaceChange?.('')}
              aria-label={`Remove workspace ${wsCurrent.label || wsCurrent.id}`}
              data-testid={`agent-workspace-remove-${wsCurrent.id}`}
              className="shrink-0 px-1 text-indigo-800 hover:text-indigo-950 focus:outline-none"
            >
              ×
            </button>
          </span>
        ) : null}
        {entries.length === 0 && mcpEntries.length === 0 && !wsCurrent ? (
          <span className="self-center text-xs text-gray-400">No tools added yet.</span>
        ) : null}
        <button
          type="button"
          onClick={() => { setPickerModal(true); setMcpChoose(null); setWsChoose(false); setConnChoose(null); }}
          data-testid="agent-tool-picker-add"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-700"
        >
          + Add a tool
        </button>
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid="agent-tool-picker-error">
          {error}
        </p>
      )}

      {/* Add-tool modal — the SHARED ToolSelectDialog (identical view + tool list
          to Connections "Create New"). Picking an MCP routes to its instance
          sub-view (auth) or one-click enable (unauth); a connector opens its
          config wizard or attaches. Rendered as the dialog's ``children`` slot
          when an MCP instance is being chosen. */}
      <ToolSelectDialog
        open={pickerModal}
        entries={selectEntries}
        onPick={onSelectEntry}
        onCancel={() => { setPickerModal(false); setMcpChoose(null); setWsChoose(false); setConnChoose(null); }}
        title="Add a tool"
        subtitle="Pick a tool to connect. MCP tools are the modern, recommended path."
        testidPrefix="agent-tool-add"
        dialogTestId="agent-tool-add-modal"
        cardTestId={(t) => (t.__mcp ? `agent-mcp-suggestion-${t.__mcp.id}` : `agent-tool-suggestion-${t.name}`)}
        headerOverride={
          mcpChoose ? `Choose a ${mcpChoose.displayName || mcpChoose.id} connection`
            : (wsChoose ? 'Choose a shared workspace'
              : (connChoose ? `Choose a ${connChoose.name} connection` : null))
        }
      >
        {/* All three selection sub-views share ONE component — config is
            normalized at wizard level, so assignment is uniformly "pick a named
            connection". Each just maps its rows to {key,label,tag,disabled}. */}
        {wsChoose ? (
          <InstanceListSubview
            onBack={() => setWsChoose(false)}
            testidPrefix="agent-workspace-instance"
            hoverClass="hover:border-indigo-400 hover:bg-indigo-50/40"
            banner={hasConnector
              ? 'This agent uses a git connector, which owns the on-disk workspace. A connector takes precedence — a workspace you pick here won’t be used at run time until you remove the connector.'
              : null}
            items={(workspaceCatalog || []).map((w) => ({
              key: String(w.id),
              label: w.label || w.id,
              tag: String(w.id) === wsId ? 'current' : 'workspace',
              scope: w.scope || null,
              disabled: String(w.id) === wsId,
            }))}
            onPick={(id) => {
              // If a connector already owns the on-disk tree, picking a
              // workspace would be silently ignored at run time — confirm first.
              if (hasConnector) { setConflictPrompt({ kind: 'workspace', id }); return; }
              onWorkspaceChange?.(id); setPickerModal(false); setWsChoose(false);
            }}
            onCreate={onNavigateToConnections
              ? () => { setPickerModal(false); setWsChoose(false); onNavigateToConnections({ tool: 'workspace' }); }
              : null}
            createLabel="+ Create a shared workspace in Connections"
            emptyHint={<>No workspaces found. Create one in <span className="font-medium">Connections</span> (upload a zip/tar or start from a template), then attach it here.</>}
          />
        ) : null}
        {mcpChoose ? (
          <InstanceListSubview
            onBack={() => setMcpChoose(null)}
            testidPrefix={`agent-mcp-instance-${mcpChoose.id}`}
            items={(mcpInstances[mcpChoose.id] || []).map((inst) => {
              const attached = mcpAttached(mcpChoose.id, inst.instanceId);
              return {
                key: inst.instanceId,
                // Preserve the legacy test-id shape ``…-{mcpId}_{instanceId}``.
                testid: `agent-mcp-instance-${mcpChoose.id}_${inst.instanceId}`,
                label: inst.label,
                tag: attached ? 'attached' : null,
                scope: inst.scope || null,
                disabled: attached,
              };
            })}
            onPick={(instanceId) => {
              const inst = (mcpInstances[mcpChoose.id] || []).find((i) => i.instanceId === instanceId);
              attachMcp(mcpChoose, instanceId, inst?.label);
              setPickerModal(false); setMcpChoose(null);
            }}
            onCreate={onNavigateToConnections
              ? () => { const id = mcpChoose.id; setPickerModal(false); setMcpChoose(null); onNavigateToConnections({ tool: id }); }
              : null}
            createLabel={`+ Add a ${mcpChoose.displayName || mcpChoose.id} connection in Connections`}
            emptyHint={<>No {mcpChoose.displayName || mcpChoose.id} connections found. Add one in <span className="font-medium">Connections</span> first, then attach it here.</>}
          />
        ) : null}
        {connChoose ? (
          <InstanceListSubview
            onBack={() => setConnChoose(null)}
            testidPrefix={`agent-connector-instance-${connChoose.name}`}
            onCreate={() => openConnectorCreate(connChoose)}
            banner={(connChoose.name === 'code-connector' && wsCurrent)
              ? `A git connector owns the on-disk workspace and takes precedence over the attached workspace “${wsCurrent.label || wsCurrent.id}”, which won’t be used at run time. You can keep both, but the workspace stays dormant until the connector is removed.`
              : null}
            items={(connInstances[connChoose.name] || []).map((inst) => {
              const ok = String(inst.testStatus || '').toUpperCase() === 'TESTED_OK';
              return {
                key: inst.secretId,
                testid: `agent-connector-instance-${connChoose.name}_${inst.secretId}`,
                label: inst.secretName || inst.secretId,
                tag: ok ? null : 'not tested',
                scope: inst.scope || null,
                disabled: !ok,
                disabledReason: 'This connection has not passed its test yet — open it in Connections to test.',
              };
            })}
            onPick={(secretId) => {
              const inst = (connInstances[connChoose.name] || []).find((i) => i.secretId === secretId);
              if (inst) attachConnector(connChoose, inst);
            }}
            emptyHint={<>No {connChoose.name} connections found. Create one below or in <span className="font-medium">Connections</span>.</>}
          />
        ) : null}
      </ToolSelectDialog>

      {/* Overlap confirm — connector and workspace both claim /workspace/repo.
          Connector wins, so the workspace would be dormant. Inform + let the
          user choose: keep both (workspace dormant) / remove the other / cancel. */}
      {conflictPrompt ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4" role="alertdialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h2 className="text-sm font-semibold text-gray-900">Connector and workspace overlap</h2>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              A git connector and a shared workspace both provide the agent’s on-disk
              files, and the connector takes precedence. If you keep both, the
              <span className="font-medium"> workspace stays dormant</span> — it won’t be
              used at run time until the connector is removed.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" data-testid="agent-conflict-cancel"
                onClick={() => setConflictPrompt(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              {/* Remove the OTHER side so there's no overlap. */}
              {conflictPrompt.kind === 'workspace' ? (
                <button type="button" data-testid="agent-conflict-remove-other"
                  onClick={() => {
                    onChange(entries.filter((e) => entryName(e) !== 'code-connector'));
                    onWorkspaceChange?.(conflictPrompt.id);
                    setConflictPrompt(null); setPickerModal(false); setWsChoose(false);
                  }}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
                  Remove connector, use workspace
                </button>
              ) : (
                <button type="button" data-testid="agent-conflict-remove-other"
                  onClick={() => {
                    onWorkspaceChange?.('');
                    attachConnector(conflictPrompt.tool, conflictPrompt.inst, { force: true });
                    setConflictPrompt(null);
                  }}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
                  Remove workspace, use connector
                </button>
              )}
              {/* Keep both (the picked side attaches; the workspace ends up dormant). */}
              <button type="button" data-testid="agent-conflict-keep-both"
                onClick={() => {
                  if (conflictPrompt.kind === 'workspace') {
                    onWorkspaceChange?.(conflictPrompt.id);
                    setPickerModal(false); setWsChoose(false);
                  } else {
                    attachConnector(conflictPrompt.tool, conflictPrompt.inst, { force: true });
                  }
                  setConflictPrompt(null);
                }}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
                Keep both
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingRemoveIdx !== null ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="agent-tool-remove-title"
        >
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h2 id="agent-tool-remove-title" className="text-sm font-semibold text-gray-900">
              Remove tool connection?
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              This connection uses stored credential material or a reusable registry attachment. Removing it detaches the
              tool from the agent in the workbench — underlying secrets remain in your tenant until deleted separately.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                data-testid="agent-tool-remove-cancel"
                onClick={cancelPendingRemove}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="agent-tool-remove-confirm"
                onClick={confirmPendingRemove}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {dialog && (
        <ToolConfigDialog
          open
          tool={dialog.tool}
          isNewEntry={dialog.isNewEntry}
          initialEntry={dialog.initialEntry}
          networkId={networkId}
          agentId={agentId}
          env={env}
          tenantId={tenantId}
          onSave={handleDialogSave}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Inline MCP credential EDIT — chip "Edit" opens the SAME wizard as
          Connections, pre-filled on THIS instance (Option A, full parity). */}
      {mcpEditWizard ? (
        <McpCredentialWizardDialog
          mcp={mcpEditWizard.mcp}
          scope={mcpEditWizard.scope}
          initialLabel={mcpEditWizard.label}
          initialValues={mcpEditWizard.initialValues}
          initialSecretsSet={mcpEditWizard.initialSecretsSet}
          onSave={handleMcpEditSave}
          onTest={handleMcpEditTest}
          onCancel={() => setMcpEditWizard(null)}
        />
      ) : null}
    </>
  );
}
